/**
 * @module SharedValidators
 * @package @geenius/i18n
 * @description Zod 4 validation schemas for the framework-agnostic i18n
 * runtime, admin inputs, and provider-compatible records.
 */

import { z } from "zod";
import { SUPPORTED_LOCALES } from "./constants";
import type {
  Direction,
  I18nConfig,
  I18nNamespace,
  Locale,
  LocaleStat,
  MissingKey,
  MissingKeyContext,
  MissingKeyContextValue,
  TranslationDict,
  TranslationEntry,
} from "./types";

const localeValues = SUPPORTED_LOCALES as unknown as [Locale, ...Locale[]];
const namespacePattern = /^[A-Za-z0-9][A-Za-z0-9_.:-]*$/;
const translationKeyPattern =
  /^[A-Za-z0-9][A-Za-z0-9_-]*(?:\.[A-Za-z0-9][A-Za-z0-9_-]*)*$/;
const maxBulkImportDepth = 8;
const maxBulkImportKeys = 2_000;
const maxBulkImportStringLength = 10_000;
const maxBulkImportKeysLabel = maxBulkImportKeys.toLocaleString("en-US");
const maxBulkImportStringLengthLabel =
  maxBulkImportStringLength.toLocaleString("en-US");

type TranslationDictLimits = {
  depth: number;
  keys: number;
  longestString: number;
};

function inspectTranslationDictLimits(
  value: TranslationDict,
  depth = 1,
): TranslationDictLimits {
  return Object.values(value).reduce<TranslationDictLimits>(
    (limits, entry) => {
      if (typeof entry === "string") {
        return {
          depth: Math.max(limits.depth, depth),
          keys: limits.keys + 1,
          longestString: Math.max(limits.longestString, entry.length),
        };
      }

      const nested = inspectTranslationDictLimits(entry, depth + 1);

      return {
        depth: Math.max(limits.depth, nested.depth),
        keys: limits.keys + nested.keys,
        longestString: Math.max(limits.longestString, nested.longestString),
      };
    },
    { depth, keys: 0, longestString: 0 },
  );
}

/**
 * Runtime validator for supported Geenius locale codes.
 */
export const localeSchema = z.enum(localeValues);

/**
 * Runtime validator for text direction metadata.
 */
export const directionSchema = z.enum([
  "ltr",
  "rtl",
]) satisfies z.ZodType<Direction>;

/**
 * Runtime validator for namespace identifiers used in loaders and providers.
 */
export const i18nNamespaceSchema = z
  .string()
  .trim()
  .min(1, "Namespace is required")
  .max(128, "Namespace must be 128 characters or fewer")
  .regex(
    namespacePattern,
    "Namespace must start with a letter or number and contain only letters, numbers, dot, underscore, colon, or hyphen",
  ) satisfies z.ZodType<I18nNamespace>;

/**
 * Namespace validator for filesystem and URL path segments.
 */
export const safeI18nNamespaceSchema = i18nNamespaceSchema.refine(
  (namespace) => !namespace.includes(".."),
  "Namespace must not contain traversal segments",
);

/**
 * Runtime validator for dot-delimited translation keys.
 */
export const translationKeySchema = z
  .string()
  .trim()
  .min(1, "Translation key is required")
  .max(512, "Translation key must be 512 characters or fewer")
  .regex(
    translationKeyPattern,
    "Translation key must be dot-delimited segments containing letters, numbers, underscore, or hyphen",
  );

/**
 * Recursive validator for nested translation dictionaries.
 */
export const translationDictSchema: z.ZodType<TranslationDict> = z.lazy(() =>
  z.record(z.string().min(1), z.union([z.string(), translationDictSchema])),
);

/**
 * Recursive translation dictionary validator with shared resource limits.
 */
export const boundedTranslationDictSchema = translationDictSchema.superRefine(
  (translations, context) => {
    const limits = inspectTranslationDictLimits(translations);

    if (limits.keys > maxBulkImportKeys) {
      context.addIssue({
        code: "custom",
        message: `Translation payload must contain ${maxBulkImportKeysLabel} translation keys (${maxBulkImportKeys} translation keys) or fewer`,
      });
    }

    if (limits.depth > maxBulkImportDepth) {
      context.addIssue({
        code: "custom",
        message: `Translation payload nesting must be ${maxBulkImportDepth} levels or fewer`,
      });
    }

    if (limits.longestString > maxBulkImportStringLength) {
      context.addIssue({
        code: "custom",
        message: `Translation values must be ${maxBulkImportStringLengthLabel} characters or fewer`,
      });
    }
  },
);

/**
 * Validator for structured missing-key context values.
 */
export const missingKeyContextValueSchema: z.ZodType<MissingKeyContextValue> =
  z.union([z.string(), z.number().finite(), z.boolean(), z.null()]);

/**
 * Validator for missing-key diagnostic context objects.
 */
export const missingKeyContextSchema: z.ZodType<MissingKeyContext> = z.record(
  z.string().min(1),
  missingKeyContextValueSchema,
);

/**
 * Validator for persisted translation rows shared by admin and provider code.
 */
export const translationEntrySchema: z.ZodType<TranslationEntry> = z.object({
  id: z.string().trim().min(1),
  locale: localeSchema,
  namespace: i18nNamespaceSchema,
  key: translationKeySchema,
  value: z.string(),
  version: z.number().int().nonnegative().optional(),
  lastEditedBy: z.string().trim().min(1).optional(),
  updatedAt: z.string().trim().min(1),
  createdAt: z.string().trim().min(1),
});

/**
 * Validator for translation create/update requests.
 */
export const translationUpsertInputSchema = z.object({
  locale: localeSchema,
  namespace: i18nNamespaceSchema,
  key: translationKeySchema,
  value: z.string(),
  lastEditedBy: z.string().trim().min(1).optional(),
});

/**
 * Validator for bulk translation import payloads.
 */
export const bulkImportTranslationsInputSchema = z.object({
  locale: localeSchema,
  namespace: i18nNamespaceSchema,
  translations: boundedTranslationDictSchema,
  lastEditedBy: z.string().trim().min(1).optional(),
});

/**
 * Validator for missing-key records returned by providers.
 */
export const missingKeySchema: z.ZodType<MissingKey> = z.object({
  id: z.string().trim().min(1),
  locale: localeSchema,
  namespace: i18nNamespaceSchema,
  key: translationKeySchema,
  context: missingKeyContextSchema.optional(),
  detectedAt: z.string().trim().min(1),
  count: z.number().int().positive(),
});

/**
 * Validator for runtime missing-key report requests.
 */
export const missingKeyReportInputSchema = z.object({
  locale: localeSchema,
  namespace: i18nNamespaceSchema,
  key: translationKeySchema,
  context: missingKeyContextSchema.optional(),
});

/**
 * Validator for locale completion statistics.
 */
export const localeStatSchema: z.ZodType<LocaleStat> = z.object({
  locale: localeSchema,
  totalKeys: z.number().int().nonnegative(),
  missingKeys: z.number().int().nonnegative(),
  coverage: z.number().min(0).max(100),
});

/**
 * Validator for shared runtime configuration.
 */
export const i18nConfigSchema: z.ZodType<I18nConfig> = z
  .object({
    defaultLocale: localeSchema,
    supportedLocales: z.array(localeSchema).min(1),
    fallbackLocale: localeSchema.optional(),
    namespaces: z.array(i18nNamespaceSchema).min(1).optional(),
    namespace: i18nNamespaceSchema.optional(),
    detectBrowser: z.boolean().optional(),
    persistLocale: z.boolean().optional(),
    dateFormat: z.string().trim().min(1).optional(),
    numberFormat: z
      .custom<Intl.NumberFormatOptions>(
        (value) =>
          typeof value === "object" && value !== null && !Array.isArray(value),
        "numberFormat must be an object",
      )
      .optional(),
    debug: z.boolean().optional(),
    missingKeyWarnings: z.boolean().optional(),
    missingKeyPrefix: z.string().optional(),
  })
  .superRefine((config, context) => {
    if (!config.supportedLocales.includes(config.defaultLocale)) {
      context.addIssue({
        code: "custom",
        path: ["defaultLocale"],
        message: "Default locale must be included in supportedLocales",
      });
    }

    if (
      config.fallbackLocale &&
      !config.supportedLocales.includes(config.fallbackLocale)
    ) {
      context.addIssue({
        code: "custom",
        path: ["fallbackLocale"],
        message: "Fallback locale must be included in supportedLocales",
      });
    }
  });

/**
 * Validator for translation loader options.
 */
export const translationLoaderConfigSchema = z.object({
  baseUrl: z.string().trim().min(1).optional(),
  cache: z.boolean().optional(),
  cacheDuration: z.number().int().positive().optional(),
  preloadAll: z.boolean().optional(),
  onError: z
    .custom<(error: unknown) => TranslationDict | undefined>(
      (value) => typeof value === "function",
      "onError must be a function",
    )
    .optional(),
});

/**
 * Raw input accepted by the locale validator before it is narrowed to Locale.
 */
export type LocaleInput = z.input<typeof localeSchema>;
/**
 * Raw input accepted by the runtime i18n configuration validator.
 */
export type I18nConfigInput = z.input<typeof i18nConfigSchema>;
/**
 * Raw input accepted by the translation entry validator.
 */
export type TranslationEntryInput = z.input<typeof translationEntrySchema>;
/**
 * Raw input accepted by the translation upsert validator.
 */
export type TranslationUpsertInput = z.input<
  typeof translationUpsertInputSchema
>;
/**
 * Raw input accepted by the bulk translation import validator.
 */
export type BulkImportTranslationsInput = z.input<
  typeof bulkImportTranslationsInputSchema
>;
/**
 * Raw input accepted by the persisted missing-key validator.
 */
export type MissingKeyInput = z.input<typeof missingKeySchema>;
/**
 * Raw input accepted by the runtime missing-key report validator.
 */
export type MissingKeyReportInput = z.input<typeof missingKeyReportInputSchema>;
/**
 * Raw input accepted by the locale statistics validator.
 */
export type LocaleStatInput = z.input<typeof localeStatSchema>;
